import React from 'react';
import { useTranslations } from '../translations/translations';
import { useFadeIn } from '../hooks/useFadeIn';
import { useLocalizedNavigate } from '../contexts/LanguageContext';

const ServicesMinimal = () => {
  const navigate = useLocalizedNavigate();
  const t = useTranslations();
  const [servicesRef, servicesVisible] = useFadeIn(0.1);
  const [moyensRef, moyensVisible] = useFadeIn(0.1);

  const services = [
    {
      number: t.services.cpo.number,
      title: t.services.cpo.title,
      subtitle: t.services.cpo.subtitle,
      subtitle2: "(Titulaire carte A3P - Agent de Protection Physique des Personnes)",
      description: t.services.cpo.description,
      imageSlot: "/images/altes/clients-event-blurred.jpeg",
      link: "/cpo"
    },
    {
      number: "02",
      title: "Risk Assessment",
      subtitle: "Risk Assessment",
      description: t.services.risk.description,
      imageSlot: "/images/altes/office-interior-blurred-face.png",
      link: "/evaluation-risques"
    },
    {
      number: "03",
      title: "Supervision",
      subtitle: "Supervision",
      description: t.services.supervision.description,
      imageSlot: "/images/altes/supervision-coordination.jpg",
      link: "/coordination-prealable"
    }
  ];

  const moyens = [
    {
      number: t.moyens.coordination.number,
      title: t.moyens.coordination.title,
      subtitle: t.moyens.coordination.subtitle,
      description: t.moyens.coordination.description,
      imageSlot: "/images/altes/cctv-toc-center.jpg",
      link: "/cctv-24-7"
    },
    {
      number: t.moyens.formation.number,
      title: t.moyens.formation.title,
      subtitle: t.moyens.formation.subtitle,
      description: t.moyens.formation.description,
      imageSlot: "/images/altes/formation-sf3.jpg",
      imageStyle: {
        objectFit: 'cover',
        objectPosition: 'center 35%'
      },
      link: "/formation"
    },
    {
      number: t.moyens.vehicules.number,
      title: t.moyens.vehicules.title,
      subtitle: t.moyens.vehicules.subtitle,
      description: t.moyens.vehicules.description,
      imageSlot: "/images/altes/vehicles-armored.png",
      link: "/vehicules-blindes"
    }
  ];

  const renderCards = (items) => (
    <div className="services-cards-grid">
      {items.map((service) => (
        <div 
          key={service.number}
          onClick={() => navigate(service.link)}
          style={{
            background: '#0a0a0a',
            border: '1px solid #262626',
            borderRadius: '8px',
            overflow: 'hidden',
            transition: 'all 0.4s ease',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#404040';
            e.currentTarget.style.transform = 'translateY(-4px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#262626';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          {/* Image */}
          <div style={{
            width: '100%',
            height: '350px',
            background: '#000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            position: 'relative'
          }}>
            {service.imageSlot ? (
              <img 
                src={service.imageSlot}
                alt={service.title}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: service.imageStyle?.objectFit || 'cover',
                  objectPosition: service.imageStyle?.objectPosition || 'center',
                  filter: 'grayscale(100%)'
                }}
              />
            ) : (
              <div style={{
                width: '100%',
                height: '100%',
                background: '#000',
                border: '2px dashed #1a1a1a'
              }} />
            )}
            
            {/* SF3 Pro Badge */}
            {service.hasSF3Logo && (
              <div style={{
                position: 'absolute',
                bottom: '1rem',
                right: '1rem',
                background: 'rgba(0, 0, 0, 0.9)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                padding: '0.75rem 1.25rem',
                borderRadius: '4px',
                fontSize: '0.85rem',
                fontWeight: 600,
                letterSpacing: '1px',
                color: '#fff'
              }}>
                Certifié SF3 Pro
              </div>
            )}
          </div>

          {/* Content */}
          <div style={{ padding: '3rem' }}>
            <div style={{
              fontSize: '0.75rem',
              letterSpacing: '2px',
              color: '#525252',
              marginBottom: '1.5rem',
              fontWeight: 500
            }}>
              {service.number}
            </div>

            <h3 style={{
              fontSize: '2rem',
              fontWeight: 300,
              marginBottom: '0.5rem',
              letterSpacing: '-0.01em',
              color: '#fff'
            }}>
              {service.title}
            </h3>

            <h4 style={{
              fontSize: '1.1rem',
              fontWeight: 400,
              marginBottom: service.subtitle2 ? '0.5rem' : '1.5rem',
              color: '#a3a3a3'
            }}>
              {service.subtitle}
            </h4>

            {service.subtitle2 && (
              <h5 style={{
                fontSize: '0.9rem',
                fontWeight: 400,
                marginBottom: '1.5rem',
                color: '#737373',
                fontStyle: 'italic'
              }}>
                {service.subtitle2}
              </h5>
            )}

            <p style={{
              fontSize: '1rem',
              color: '#737373',
              lineHeight: 1.8,
              fontWeight: 300
            }}>
              {service.description}
            </p>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      {/* Section Nos Services */}
      <section 
        id="services" 
        ref={servicesRef}
        className={`fade-in-section ${servicesVisible ? 'is-visible' : ''}`}
        style={{ padding: '10rem 0 5rem', background: '#000' }}
      >
        <div className="container-custom">
          <div className="section-header">
            <h2 className="section-title">{t.services.title}</h2>
            <p className="section-subtitle">
              {t.services.subtitle}
            </p>
          </div>

          {renderCards(services)}
        </div>
      </section>

      {/* Section Nos Moyens */}
      <section 
        id="moyens" 
        ref={moyensRef}
        className={`fade-in-section ${moyensVisible ? 'is-visible' : ''}`}
        style={{ padding: '5rem 0 10rem', background: '#000' }}
      >
        <div className="container-custom">
          <div className="section-header">
            <h2 className="section-title">{t.moyens.title}</h2>
            <p className="section-subtitle">
              {t.moyens.subtitle}
            </p>
          </div>

          {renderCards(moyens)}
        </div>
      </section>
    </>
  );
};

export default ServicesMinimal;
